import { format } from "date-fns";

// Leading chars a spreadsheet app may execute as a formula; we prefix them with ' (CSV injection defense).
const CSV_FORMULA_TRIGGERS = new Set(["=", "+", "-", "@", "\t", "\r"]);

export const CSV_EXPORT_MAX_ROWS = 10_000;

/** Set when an export hit CSV_EXPORT_MAX_ROWS; a CSV body has nowhere to carry that flag. */
export const CSV_EXPORT_CAPPED_HEADER = "x-export-capped";

export const CSV_EXPORT_CAPPED_MESSAGE = `Export capped at ${CSV_EXPORT_MAX_ROWS.toLocaleString("en-US")} rows. Add filters to narrow it.`;

export enum CsvExportDataset {
  REFERRALS = "referrals",
  APPOINTMENTS = "appointments",
  ANALYTICS = "analytics",
  COMMUNICATIONS = "communications",
}

/**
 * `valerie_<yyyy-MM-dd_HHmm>_<dataset>[_<qualifier>].csv`, stamped in the caller's own clock: the
 * browser's zone for the download name, the container's (UTC) for a route's Content-Disposition.
 */
export function buildCsvExportFilename({
  dataset,
  qualifier,
  now = new Date(),
}: {
  dataset: CsvExportDataset;
  qualifier?: string;
  now?: Date;
}): string {
  const parts = ["valerie", format(now, "yyyy-MM-dd_HHmm"), dataset, qualifier?.replace(/-/g, "_")];

  return `${parts.filter(Boolean).join("_")}.csv`;
}

/**
 * Spreadsheet-injection defense only (no CSV quoting): prefixes `'` when the value starts with a
 * character Excel/Sheets would execute as a formula. Use this on cell values handed to a CSV
 * library that does its own quoting (e.g. csv-writer) — quoting alone does NOT stop execution.
 */
export function neutralizeCsvFormula(value: string): string {
  return CSV_FORMULA_TRIGGERS.has(value[0] ?? "") ? `'${value}` : value;
}

export function csvEscape(value: string): string {
  const neutralized = neutralizeCsvFormula(value);

  if (
    neutralized.includes('"') ||
    neutralized.includes(",") ||
    neutralized.includes("\n") ||
    neutralized.includes("\r")
  ) {
    return `"${neutralized.replace(/"/g, '""')}"`;
  }

  return neutralized;
}

export function buildSingleRowCsv(header: string[], cells: string[] = []): string {
  const headerLine = header.map(csvEscape).join(",");
  // Pad short rows / drop overflow so the stored per-row CSV stays rectangular.
  const alignedCells = header.map((_, i) => cells[i] ?? "");
  const dataLine = alignedCells.map(csvEscape).join(",");

  return `${headerLine}\n${dataLine}\n`;
}

export const CSV_CONTENT_TYPE = "text/csv";

export function isCsvContentType(contentType?: string | null): boolean {
  if (!contentType) return false;

  return contentType.split(";")[0].trim().toLowerCase() === CSV_CONTENT_TYPE;
}
